import { useState } from "react"
import { Link } from "@inertiajs/react"
import { ChevronRight, KeyRound, Loader2, ShieldCheck } from "lucide-react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { postInline, toastInlineUpdateError, toastInlineUpdateSuccess } from "@/lib/inline-update"
import { t } from "@/lib/i18n"

type SecurityCardProps = {
  /** Whether this diner ever chose their own password. See User#password_set?. */
  hasPassword: boolean
  /** OmniAuth provider keys, e.g. `google_oauth2`. */
  connectedProviders: string[]
}

// Brand names, so they stay out of the translation layer for the same reason
// LocaleSelector spells languages as endonyms. An unmapped provider falls back
// to its own key, which still tells the reader more than a blank line would.
const PROVIDER_NAMES: Record<string, string> = {
  google_oauth2: "Google",
}

function providerLabel(provider: string) {
  return PROVIDER_NAMES[provider] ?? provider
}

/**
 * Security lives here rather than in the hub, and it navigates rather than
 * inlining a form.
 *
 * Not in the hub, because rotating a credential is not a peer of "Recibos" —
 * it belongs with the account it protects. Not inline, because changing a
 * password re-authenticates: it asks for the current one, which the identity
 * form does not. Folding both into one submit would put two different security
 * postures behind one button, and it would break the heuristic browsers and
 * password managers use to recognise a change-password form and offer to update
 * the stored credential. So `/profile/password` stays an isolated screen and
 * this card only points at it.
 *
 * The entry is offered only to someone who has a password to change. Google
 * sign-ins and admin invitations are provisioned with a random value nobody has
 * ever seen, and the user domain has no password reset — offering them that
 * screen offers a door that cannot open. They get told what actually signs them
 * in instead.
 */
export function SecurityCard({ hasPassword, connectedProviders }: SecurityCardProps) {
  const [sending, setSending] = useState(false)

  // A plain `<Link method="post">` here looked broken, and the reason is worth
  // keeping: the endpoint mails a link and redirects straight back to this
  // screen with a flash, but the diner domain has no global flash renderer, so
  // the page re-rendered identically and the tap read as a dead control. Going
  // through `postInline` surfaces the server's own copy as a toast, which is how
  // every other one-shot diner action reports itself.
  //
  // The pending state is not decoration. The endpoint is rate limited to five
  // requests per fifteen minutes, so a diner who taps a silent button four more
  // times locks themselves out of the recovery they were asking for.
  const requestPasswordLink = () => {
    setSending(true)
    postInline("/passwords")
      .then(toastInlineUpdateSuccess)
      .catch(toastInlineUpdateError)
      .finally(() => setSending(false))
  }

  return (
    <Card variant="borderless" data-testid="profile-security-card">
      <CardHeader>
        <CardTitle>{t("ui.react.components.features.profiles.security_card.title")}</CardTitle>
        <CardDescription>
          {t("ui.react.components.features.profiles.security_card.description")}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col gap-1">
          {hasPassword && (
            <Link
              href="/profile/password"
              data-testid="profile-security-password"
              className="flex min-h-11 items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted/50 hover:text-foreground md:min-h-0"
            >
              <KeyRound className="h-4 w-4 shrink-0" />
              <span className="flex-1 text-left">
                {t("ui.react.components.features.profiles.profile_sidebar.change_password")}
              </span>
              <ChevronRight className="h-4 w-4 shrink-0" />
            </Link>
          )}

          {connectedProviders.length > 0 && (
            <div
              className="flex min-h-11 items-center gap-3 px-3 py-2.5 text-sm text-muted-foreground md:min-h-0"
              data-testid="profile-security-providers"
            >
              <ShieldCheck className="h-4 w-4 shrink-0" />
              <span>
                {t("ui.react.components.features.profiles.security_card.signs_in_with", {
                  providers: connectedProviders.map(providerLabel).join(", "),
                })}
              </span>
            </div>
          )}

          {!hasPassword && connectedProviders.length === 0 && (
            <p
              className="px-3 py-2.5 text-sm text-muted-foreground"
              data-testid="profile-security-empty"
            >
              {t("ui.react.components.features.profiles.security_card.no_password")}
            </p>
          )}

          {/* The card used to state the provider and stop there, which left the
              diner depending on it forever. This mails a link rather than
              setting a password in place: proving control of the address is the
              point, or a hijacked provider session could mint a password that
              outlives revoking the provider. */}
          {!hasPassword && (
            <button
              type="button"
              onClick={requestPasswordLink}
              disabled={sending}
              data-testid="profile-security-create-password"
              className="flex min-h-11 items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted/50 hover:text-foreground disabled:pointer-events-none disabled:opacity-60 md:min-h-0"
            >
              {sending ? (
                <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
              ) : (
                <KeyRound className="h-4 w-4 shrink-0" />
              )}
              <span className="flex-1 text-left">
                {sending
                  ? t("ui.react.components.features.profiles.security_card.sending")
                  : t("ui.react.components.features.profiles.security_card.create_password")}
              </span>
            </button>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
